/**
 * Failure Mode and Effects Analysis — the top failure modes per machine class.
 *
 * Twelve rows covering what the fleet actually breaks on (brakes, turbo and
 * engine derate, pump-drive couplings, GET, rock-drill front ends, rod
 * handling, DTH hammers, rotary heads, battery modules, charging connectors,
 * transmissions). Severity and the **base** detection score are
 * proof-of-concept engineering judgement. **Occurrence is not taken from this
 * table** when the engine can derive it: `shared/reliability/fmea.ts` counts
 * breakdown and corrective work orders per class × component in
 * `work-orders.json` and scores the per-machine-year rate. The `occurrence`
 * here is only the fallback when no work order matches. Detection is lowered
 * by the engine where a TARP or an SPC chart watches the component.
 *
 * `match` is a case-insensitive regex source applied to the work-order and
 * condition-register component names — `'^engine$'` so a truck's `Engine` does
 * not also count a jumbo's `Carrier engine (diesel)`.
 *
 * Data Schema layer — seed data.
 *
 * @see src/versions/v1/schema/operations.ts — `FmeaRow`
 */

import type { FmeaRow } from '../../schema/operations.ts';

/** A seeded FMEA row: everything but the RPN, plus the component matcher. */
export type FmeaSeed = Omit<FmeaRow, 'rpn'> & { readonly match: string };

/** The seeded failure modes. */
export const FMEA_SEED: readonly FmeaSeed[] = [
  {
    id: 'FM-TRK-BRK-01',
    class: 'truck',
    component: 'Retarder & service brakes',
    match: 'retarder|service brake',
    failureMode: 'Brake fade — pads past wear limit on loaded descent',
    effect: 'Loss of retardation on the decline; disc and hub-seal damage; runaway risk',
    cause: 'Pad wear running ahead of interval; service brake used in place of the retarder on the 1:7',
    severity: 9,
    occurrence: 5,
    detection: 5,
    currentControls: 'Pad thickness at PM-500; live brake temperature on connected sites',
    recommendedAction: 'TARP-BRK-TEMP on every decline truck; pad check moved to PM-250; operator descent refresher where exceedances concentrate',
    owner: 'Reliability Engineer',
  },
  {
    id: 'FM-TRK-TRN-01',
    class: 'truck',
    component: 'Transmission',
    match: 'transmission',
    failureMode: 'Clutch pack wear',
    effect: 'Gear slip on the ramp; transmission change-out',
    cause: 'Wrong-gear selection and brake-throttle overlap on the decline',
    severity: 6,
    occurrence: 4,
    detection: 6,
    currentControls: 'Transmission oil analysis at PM-500',
    recommendedAction: 'Brake-throttle overlap and wrong-gear counters reviewed weekly by the Operating Supervisor',
    owner: 'Operating Supervisor',
  },
  {
    id: 'FM-TRK-ENG-01',
    class: 'truck',
    component: 'Engine',
    match: '^engine$',
    failureMode: 'Turbocharger failure',
    effect: 'Engine derate on the ramp; secondary engine damage if run on',
    cause: 'Oil contamination; hot shutdown without cool-down',
    severity: 7,
    occurrence: 4,
    detection: 6,
    currentControls: 'Oil analysis; derate alarm',
    recommendedAction: 'Boost-pressure trend on connected sites; three-minute cool-down in the pre-start card',
    owner: 'Reliability Engineer',
  },
  {
    id: 'FM-LHD-HYD-01',
    class: 'lhd',
    component: 'Hydraulic pump & drive',
    match: 'hydraulic pump',
    failureMode: 'Pump-drive coupling failure',
    effect: 'Loss of hydraulics — loader stranded at the face',
    cause: 'Shock loading from bucket ramming and boom overload; spline wear; oil over temperature',
    severity: 7,
    occurrence: 5,
    detection: 6,
    currentControls: 'Coupling inspection at PM-500; hydraulic oil temperature at shift end',
    recommendedAction: 'TARP-HYD-TEMP; ramming and boom-overload coaching; coupling added to the exchange pool',
    owner: 'Reliability Engineer',
  },
  {
    id: 'FM-LHD-BKT-01',
    class: 'lhd',
    component: 'Bucket & GET',
    match: 'bucket & get',
    failureMode: 'GET loss and bucket lip cracking',
    effect: 'GET in the ore pass or crusher; bucket rebuild',
    cause: 'Ramming the muck pile; poor floor preparation',
    severity: 6,
    occurrence: 4,
    detection: 5,
    currentControls: 'Visual at pre-start',
    recommendedAction: 'RMS ramming counter reviewed per operator; trainer ride-along where the ratio to peers is ≥ 1.8',
    owner: 'Operating Supervisor',
  },
  {
    id: 'FM-LHD-ENG-01',
    class: 'lhd',
    component: 'Engine',
    match: '^engine$',
    failureMode: 'Engine derate — high intake air temperature',
    effect: 'Reduced tramming speed; shutdown on hot levels',
    cause: 'Blocked air filter; ventilation on the working level',
    severity: 5,
    occurrence: 4,
    detection: 5,
    currentControls: 'Derate alarm; air filter at PM-250',
    recommendedAction: 'Air filter restriction trended on connected loaders',
    owner: 'Maintenance Planner',
  },
  {
    id: 'FM-JUM-RD-01',
    class: 'jumbo',
    component: 'Rock drill (LH / RH)',
    match: 'rock drill',
    failureMode: 'Rock drill front-end failure',
    effect: 'Front end destroyed rather than exchanged; shank and a boom hose with it; the cut stops',
    cause: 'Front-end wear; accumulator pre-charge loss; flushing leak at the front cover',
    severity: 7,
    occurrence: 5,
    detection: 5,
    currentControls: 'Accumulator pre-charge at PM-250; percussion pressure trend (TB-2026-027)',
    recommendedAction: 'TARP-PERC-PRESS; exchange front end at the cut change once Level 2 trips',
    owner: 'Reliability Engineer',
  },
  {
    id: 'FM-LHL-ROD-01',
    class: 'longhole',
    component: 'Rod carousel',
    match: 'rod carousel|rod changer',
    failureMode: 'Rod drop or carousel jam',
    effect: 'Rod lost in the hole; ring re-drilled',
    cause: 'Over-torque make-ups; worn gripper jaws',
    severity: 6,
    occurrence: 4,
    detection: 4,
    currentControls: 'Gripper inspection at PM-500',
    recommendedAction: 'Over-torque make-up counter reviewed per operator',
    owner: 'Operating Supervisor',
  },
  {
    id: 'FM-SDR-DTH-01',
    class: 'sdrill',
    component: 'DTH hammer & pipes',
    match: 'dth|hammer',
    failureMode: 'DTH hammer piston wear',
    effect: 'Hammer and pipes lost in the hole; fishing job',
    cause: 'Piston and wear-sleeve wear; low air; poor flushing',
    severity: 8,
    occurrence: 4,
    detection: 5,
    currentControls: 'Penetration rate reported daily by RMS',
    recommendedAction: 'SPC on penetration rate at constant air; exchange hammer at the pattern change on the first signal',
    owner: 'Reliability Engineer',
  },
  {
    id: 'FM-SDR-ROT-01',
    class: 'sdrill',
    component: 'Rotary head',
    match: 'rotary',
    failureMode: 'Rotary head motor wear',
    effect: 'Head seizes on the string; table damage; pattern half drilled',
    cause: 'Motor wear; sustained high feed force',
    severity: 8,
    occurrence: 3,
    detection: 6,
    currentControls: 'Torque oscillation alarm',
    recommendedAction: 'Case-drain flow trended; feed force backed off by the operator on the first signal',
    owner: 'Reliability Engineer',
  },
  {
    id: 'FM-BEV-BAT-01',
    class: 'lhd',
    component: 'Battery pack (LFP, 300 kWh)',
    match: 'battery pack',
    failureMode: 'Battery module isolation fault',
    effect: 'Pack derated, fast charge locked out; one module replaced against a whole pack',
    cause: 'Coolant ingress; cell imbalance under fast charge',
    severity: 9,
    occurrence: 3,
    detection: 4,
    currentControls: 'BMS isolation monitoring',
    recommendedAction: 'TARP-BAT-ISO; no fast charge above 45 °C pack temperature',
    owner: 'Reliability Engineer',
  },
  {
    id: 'FM-BEV-CHG-01',
    class: 'lhd',
    component: 'Charging interface',
    match: 'charging',
    failureMode: 'Connector moisture — charge handshake failure',
    effect: 'Loader stranded below 40 % state of charge; tow to the bay',
    cause: 'High-pressure wash-down on the charge inlet',
    severity: 5,
    occurrence: 4,
    detection: 3,
    currentControls: 'Handshake retry logged by the charger',
    recommendedAction: 'TARP-CHG-MOIST; inlet caps at wash-down; connector change on the first fault',
    owner: 'Operating Supervisor',
  },
];
